import APITemplate from '@api/APITemplate'
import { diffTimeMinutes } from '@utils/Format'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import con from '@services/connection'
import { configJourney } from '@modules/central-config/constants/URL'

class APICentralConfig extends APITemplate {
  constructor() {
    super()
    this.reqCarregar = null
  }

  // Retorna o valor cacheado quando a janela de cache ainda é válida (ou quando
  // há um carregamento em andamento) — evita refetch redundante da jornada.
  _shouldSkipLoad(forceUpdate) {
    if (!forceUpdate) {
      const shouldUpdate = diffTimeMinutes(
        store.getters['centralConfig/journeyLastUpdate'],
        MINUTES_TIMEOUT_REQUESTS
      )
      if (!shouldUpdate) {
        return store.getters['centralConfig/journey']
      }
    }

    if (store.getters['centralConfig/journeyLoading']) {
      return store.getters['centralConfig/journey']
    }

    return null
  }

  // Falha ao carregar: zera a jornada (estado vazio consistente com o contrato)
  // e devolve um erro padrão com a solução para o usuário. Extraído para manter
  // carregarJornada dentro do limite de statements.
  _handleJourneyFailure(r) {
    store.dispatch('centralConfig/setJourney', {
      workspace: { office: '', professional: '' },
      generatedAt: '',
      modules: [],
    })
    return (
      r || {
        status: 500,
        message:
          'Não foi possível carregar a jornada. Tente novamente mais tarde ou contate o suporte.',
        value: null,
      }
    )
  }

  // Sem await direto aqui — o corpo do fetch fica em _fetchJornada (a promise
  // é atribuída a this.reqCarregar ANTES de retornar, pro dedup funcionar).
  carregarJornada({ forceUpdate = false, silent = false } = {}) {
    const skipResult = this._shouldSkipLoad(forceUpdate)
    if (skipResult && skipResult.modules && skipResult.modules.length) {
      return this.buildSuccessResponse({
        value: JSON.parse(JSON.stringify(skipResult)),
      })
    }

    if (this.reqCarregar) {
      return this.reqCarregar
    }

    // Dedup de request em voo: guarda a PROMISE (não só um boolean) para que
    // chamadas concorrentes (poll + foco + clique do usuário, por exemplo)
    // compartilhem o mesmo fetch em vez de disparar requests duplicados.
    //
    // ★ A limpeza acontece AQUI, e não no `finally` de `_fetchJornada`
    // (2026-08-07, reportado em tela: "só a primeira marcação funciona") —
    // `_fetchJornada` é async e o antigo caminho síncrono do mock (sem `await`
    // antes do `return`) fazia o `finally` rodar ANTES desta atribuição, presa
    // pra sempre. Limpar depois da atribuição, comparando a identidade da
    // promessa, segue correto agora que o único caminho é o do backend (sempre
    // cede o controle no `await con.get(...)`) e não desarma um fetch mais novo.
    const req = this._fetchJornada(silent)
    this.reqCarregar = req
    const liberar = () => {
      if (this.reqCarregar === req) this.reqCarregar = null
    }
    // Os dois callbacks: `finally` deixaria a rejeição sem handler nesta
    // derivação. `req` segue propagando o erro para quem chamou.
    req.then(liberar, liberar)
    return req
  }

  async _fetchJornada(silent) {
    // Refresh silencioso (poll/foco/pós-marcação): NÃO liga o loading global —
    // evita o "clarão" do skeleton a cada poll. Só a carga inicial mostra loading.
    if (!silent) await store.dispatch('centralConfig/setJourneyLoading', true)

    try {
      // Fase 2: backend entrega tarefas + status reais (marcação manual por
      // escritório). Mesmo shape do contrato; o front não recalcula status.
      const r = await con.get(configJourney.get())
      if (r && r.status === 200 && r.value) {
        this._applyJourney(r.value)
        // FORA do short-circuit de _applyJourney de propósito (spec-004): o
        // backend resolve/auto-avança o nível a cada GET, e ele pode mudar em
        // cenários onde a assinatura da jornada não muda (ex.: curadoria de
        // nível alterada no Interno sem mexer em status). Despachar sempre é
        // barato (uma mutation) e evita rail defasado.
        this._applyNivelAtual(r.value.nivel_atual)
        return r
      }
      return this._handleJourneyFailure(r)
    } finally {
      if (!silent) store.dispatch('centralConfig/setJourneyLoading', false)
      // `reqCarregar` NÃO é limpo aqui: dono único da limpeza é `carregarJornada`.
    }
  }

  // Só aplica a jornada (dispatch setJourney → re-render) se ela MUDOU de verdade.
  // O poll/foco busca sempre, mas trocar a referência no store quando nada mudou
  // re-renderiza tudo à toa (o "clarão"). Compara o conteúdo (modules + workspace)
  // por JSON, IGNORANDO `generatedAt` (que muda a cada fetch mesmo sem alteração
  // real — senão a assinatura nunca seria igual).
  _applyJourney(value) {
    const current = store.getters['centralConfig/journey']
    if (this._journeySignature(current) === this._journeySignature(value)) {
      return
    }
    store.dispatch('centralConfig/setJourney', value)
  }

  _journeySignature(journey) {
    const j = journey || {}
    return JSON.stringify({
      workspace: j.workspace || {},
      modules: j.modules || [],
    })
  }

  // spec-004 — aplica `value.nivel_atual` no store. Campo ADITIVO: backend que
  // ainda não o entregue (ou fail-open do usecase) simplesmente não dispara,
  // preservando o default Nível 1 do state em vez de zerar o rail.
  _applyNivelAtual(nivelAtual) {
    if (!nivelAtual) return
    store.dispatch('centralConfig/setNivelAtual', nivelAtual)
  }

  // Marca (done=true) ou desmarca (done=false) uma etapa da jornada para o
  // escritório. Reversível. Após sucesso, re-consulta a jornada com forceUpdate
  // para re-sincronizar os status curados pelo backend (o front não os calcula).
  async marcarTask({ taskId, done = true } = {}) {
    if (!taskId) {
      return this.buildErrorResponse({
        message: 'Selecione uma etapa para marcar.',
        status: 400,
        value: null,
      })
    }

    const r = await con.post(configJourney.markTask(), {
      task_id: taskId,
      done,
    })

    if (r && r.status === 200) {
      await this.carregarJornada({ forceUpdate: true, silent: true })
    }

    return (
      r || {
        status: 500,
        message:
          'Não foi possível atualizar a etapa. Tente novamente mais tarde.',
        value: null,
      }
    )
  }
}

const apiCentralConfig = new APICentralConfig()
export { apiCentralConfig }
